// Veranstaltungen (docs/veranstaltungen/SPEZIFIKATION.md) – Zustände, Bezeichnungen und reine Regeln für Server UND
// Oberfläche: Zeitraum der Leihartikel, Ampel der Verfügbarkeit, Vorschlag der Gebühren. Mengen in Tausendstel, Geld in
// Cent, Tage als 'YYYY-MM-DD'.

export const EVENT_FEATURE = 'events'

export const EVENT_STATUSES = ['inquiry', 'quoted', 'confirmed', 'delivered', 'picked_up', 'settled', 'cancelled'] as const
export type EventStatus = (typeof EVENT_STATUSES)[number]
export const EVENT_STATUS_LABELS: Record<EventStatus, string> = {
  inquiry: 'Anfrage',
  quoted: 'Angebot',
  confirmed: 'Zugesagt',
  delivered: 'Geliefert',
  picked_up: 'Abgeholt',
  settled: 'Abgerechnet',
  cancelled: 'Abgesagt',
}
/** noch nicht abgeschlossen (weder abgerechnet noch abgesagt) */
export const EVENT_OPEN_STATUSES = ['inquiry', 'quoted', 'confirmed', 'delivered', 'picked_up'] as const
/** vor der Zusage: Termine, Anschrift und Positionen lassen sich ändern */
export const EVENT_EDITABLE_STATUSES = ['inquiry', 'quoted'] as const

export const EVENT_SOURCES = ['manual', 'mail', 'phone', 'app'] as const
export type EventSource = (typeof EVENT_SOURCES)[number]
export const EVENT_SOURCE_LABELS: Record<EventSource, string> = { manual: 'Von Hand', mail: 'E-Mail', phone: 'Telefon', app: 'Bestell-App' }

export const EVENT_LINE_KINDS = ['goods', 'rental', 'fee'] as const
export type EventLineKind = (typeof EVENT_LINE_KINDS)[number]
export const EVENT_LINE_KIND_LABELS: Record<EventLineKind, string> = { goods: 'Getränke und Ware', rental: 'Leihartikel', fee: 'Gebühr' }

/** Zusatzfeld am Auftrag (und am Angebot), das ihn einer Veranstaltung zuordnet */
export const EVENT_ORDER_FIELD = 'eventId'
/** Zusatzfeld an Gebührenzeilen der Leihgebühr: Leihartikel, für den die Zeile steht (daraus entsteht bei der Zusage die Buchung) */
export const EVENT_RENTAL_FIELD = 'eventRentalItemId'
/** Zusatzfeld an Gebührenzeilen der Abrechnung: Regel bzw. Ersatz, aus der die Zeile stammt */
export const EVENT_FEE_FIELD = 'eventFee'

/** Belegart des Abholauftrags (AH-…): entsteht mit der Übergabe, Stopp der Art „Abholung“ in der Disposition (M4) */
export const EVENT_PICKUP_TYPE = 'event_pickup'
/** Art eines Stopps der Disposition: Lieferung eines Auftrags oder Abholung bei einer Veranstaltung */
export const STOP_KINDS = ['delivery', 'event_pickup'] as const
export type StopKind = (typeof STOP_KINDS)[number]

// ---- Termine -----------------------------------------------------------------------------------------------------

export interface EventDates {
  eventStart: string | null
  eventEnd: string | null
  deliveryDate: string | null
  pickupDate: string | null
}

/**
 * Zeitraum der Leihartikel: von der Anlieferung bis zur Abholung (beide Tage gebunden). Fehlt die Anlieferung, gilt der
 * Beginn, fehlt die Abholung, das Ende (sonst der Beginn). Ohne jeden Tag: null.
 */
export function rentalPeriod(dates: EventDates): { fromDate: string; toDate: string } | null {
  const from = dates.deliveryDate ?? dates.eventStart ?? dates.eventEnd ?? dates.pickupDate
  if (!from) return null
  const to = dates.pickupDate ?? dates.eventEnd ?? dates.eventStart ?? from
  return to >= from ? { fromDate: from, toDate: to } : { fromDate: from, toDate: from }
}

/** Widersprüche der Termine als Feldfehler (Pfad → Satz); leer, wenn alles passt. */
export function dateProblems(dates: EventDates): { path: string; message: string }[] {
  const problems: { path: string; message: string }[] = []
  if (dates.eventStart && dates.eventEnd && dates.eventEnd < dates.eventStart) problems.push({ path: 'eventEnd', message: 'Das Ende der Veranstaltung liegt vor ihrem Beginn.' })
  if (dates.deliveryDate && dates.eventStart && dates.deliveryDate > dates.eventStart) problems.push({ path: 'deliveryDate', message: 'Die Anlieferung liegt nach dem Beginn der Veranstaltung.' })
  if (dates.pickupDate && dates.deliveryDate && dates.pickupDate < dates.deliveryDate) problems.push({ path: 'pickupDate', message: 'Die Abholung liegt vor der Anlieferung.' })
  if (dates.pickupDate && dates.eventEnd && dates.pickupDate < dates.eventEnd) problems.push({ path: 'pickupDate', message: 'Die Abholung liegt vor dem Ende der Veranstaltung.' })
  return problems
}

// ---- Verfügbarkeit der Leihartikel ------------------------------------------------------------------------------

export const RENTAL_CHECK_STATUSES = ['available', 'tight', 'unavailable', 'open'] as const
export type RentalCheckStatus = (typeof RENTAL_CHECK_STATUSES)[number]
export const RENTAL_CHECK_LABELS: Record<RentalCheckStatus, string> = {
  available: 'Verfügbar',
  tight: 'Knapp',
  unavailable: 'Nicht verfügbar',
  open: 'Zeitraum fehlt',
}

/**
 * Ampel eines Leihartikels für eine Veranstaltung. `freeQty` = kleinste freie Menge im Zeitraum (eigener Bestand, ohne
 * die eigene Buchung der Veranstaltung), `loanQty` = bei Partnern zugeliehen (nur für diese Veranstaltung).
 *   nicht verfügbar  frei + zugeliehen < gewünscht
 *   knapp            reicht, aber danach bleibt weniger als ein Zehntel des Bestands (mindestens 1 Stück) frei
 *   verfügbar        sonst
 */
export function rentalCheckStatus(args: { requestedQty: number; freeQty: number; loanQty: number; totalQty: number; hasPeriod: boolean }): RentalCheckStatus {
  if (!args.hasPeriod) return 'open'
  const available = args.freeQty + args.loanQty
  if (available < args.requestedQty) return 'unavailable'
  const reserve = Math.max(1000, Math.ceil(args.totalQty / 10 / 1000) * 1000)
  return available - args.requestedQty < reserve ? 'tight' : 'available'
}

// ---- Gebühren ----------------------------------------------------------------------------------------------------

export const FEE_BASES = ['flat', 'per_trip', 'per_returned_unit'] as const
export type FeeBasis = (typeof FEE_BASES)[number]
export const FEE_BASIS_LABELS: Record<FeeBasis, string> = {
  flat: 'Pauschal je Veranstaltung',
  per_trip: 'Je Fahrt (Anlieferung und Abholung)',
  per_returned_unit: 'Je zurückgegebenem vollen Gebinde',
}

export interface FeeRule {
  id: string
  itemId: string
  label: string
  basis: FeeBasis
  /** nur Gebinde dieser Einheiten zählen (Codes wie 'crate', 'keg', 'bottle'); leer = alle */
  unitCodes: readonly string[]
  /** Preis aus dem Liefergebiet der Anschrift (brutto), sonst `priceCents` bzw. Preisfindung */
  useZoneFee: boolean
  active: boolean
}

export interface ReturnedUnit {
  /** Code der Einheit des Gebindes der Zeile ('crate', 'keg', 'bottle' …) */
  unitCode: string | null
  /** volle Rückware in Tausendstel des Gebindes der Zeile */
  returnedQty: number
}

/** Menge einer Gebühr nach ihrer Regel (Tausendstel): pauschal 1, je Fahrt die Zahl der Fahrten, je Gebinde die volle Rückware. */
export function feeQty(rule: Pick<FeeRule, 'basis' | 'unitCodes'>, counts: { trips: number; returned: readonly ReturnedUnit[] }): number {
  if (rule.basis === 'flat') return 1000
  if (rule.basis === 'per_trip') return Math.max(0, counts.trips) * 1000
  const codes = new Set(rule.unitCodes)
  return counts.returned
    .filter((entry) => codes.size === 0 || (entry.unitCode !== null && codes.has(entry.unitCode)))
    .reduce((sum, entry) => sum + Math.floor(Math.max(0, entry.returnedQty) / 1000) * 1000, 0)
}

/** Aus Fahrten zählen Anlieferung und Abholung, sofern sie nicht in einer Filiale stattfinden. */
export const tripsOf = (args: { delivered: boolean; pickedUp: boolean }): number => (args.delivered ? 1 : 0) + (args.pickedUp ? 1 : 0)

// ---- Texte -------------------------------------------------------------------------------------------------------

const TIME = /^([01]\d|2[0-3]):[0-5]\d$/
const time = (value: string | null | undefined): string | null => (value && TIME.test(value) ? value : null)

/** „10:00–12:00 Uhr“ bzw. „ab 10:00 Uhr“ – oder null */
export function windowText(from: string | null | undefined, to: string | null | undefined): string | null {
  const a = time(from)
  const b = time(to)
  if (a && b) return `${a}–${b} Uhr`
  if (a) return `ab ${a} Uhr`
  if (b) return `bis ${b} Uhr`
  return null
}

/** Anschrift des Veranstaltungsorts in einer Zeile */
export function venueLine(venue: { street?: string | null; postalCode?: string | null; city?: string | null } | null | undefined): string | null {
  if (!venue) return null
  const town = [venue.postalCode, venue.city].filter((part) => part && part.trim() !== '').join(' ')
  const text = [venue.street?.trim() || null, town || null].filter(Boolean).join(', ')
  return text || null
}
